// #region Imports
import { QueryResult } from '@apollo/client/react/types/types';
import { ActionMap, ModalActionButton, TypedFunction } from '@trarc/pc-shared';
import { BaseSyntheticEvent, Context, Dispatch, ReactNode } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  CheckRiskRegistryAnswerInput,
  CheckRiskRegistryAnswersQuery,
  Exact,
  InputMaybe,
  Organization,
  QuestionShorthand,
} from '../../types/api.graphql';

// #endregion

// #region Setup
/**
 * This is a generic object type that is used for each answer in the context for each question.
 */
export interface AnswerResponse<AnswerType> {
  /**
   * A string reference to the database table's ID for the answer if one exists.
   */
  answerId?: string;
  /**
   * A boolean value that when true, means that the answer has expired and needs an updated answer to the
   * respective question.
   */
  isExpired: boolean;
  /**
   * The answer to the question. The generic type is used here since in the context, we use `Record<string, boolean>`, `Date`, or `string` after
   * we have converted the answer string from the database into one of those formats based on indicators have in the database string.
   */
  answer?: AnswerType;
}

/**
 * A union type of all the possible states that the question modals can be in.
 *
 * * `start` is for the start modal that displays at the beginning of answering questions.
 * * `question` is for the question modals that contain all the questions that a user needs to answer.
 * * `close` is for when the modal is closed by the user without having finished answering questions and will display the 'Are You Sure?' modal.
 * * `done` is for when the user has finished answering all questions and should be shown the 'done' modal that allows them to generate
 *    risks for all currently answered questions.
 * * `never` is for when the modals have been closed and should never be shown again on this session, including after a refresh.
 */
export type OpenQuestionModalType = 'start' | 'question' | 'close' | 'done' | 'never' | undefined;

/**
 * The object type that is maintained by the RiskAssessmentQuestions context and can be
 * consumed by any child of the RiskAssessmentQuestions provider.
 */
export interface RiskAssessmentQuestionsContextState {
  /**
   * Whether the context has been initialized with data from the back-end. Without this, the modals could show
   * before we have retrieve data which would result in errors that crash and/or questions that should not be shown or
   * answered being shown.
   */
  isInitialized: boolean;
  /**
   * The current type of modal that should be being shown.
   */
  openModalType: OpenQuestionModalType;
  /**
   * The current step that the user is on in the question modals.
   */
  activeStep: number;
  /**
   * The previous step that the user was on prior to progressing to the current step. This is needed for the modals
   * that had to be split into multiple parts because they were too tall to for normal screen sizes. These modals don't actually progress
   * to a new step, but do change what questions are shown when clicking the next button.
   */
  prevActiveStep: number;
  /**
   * A boolean for whether the organization is currently onboarding still of if they have finished.
   */
  isOnboarding: boolean;
  /**
   * The total number of questions that remain. Since the questions shown can be any number and change each time you answer questions, we save the
   * value here after calculating how many should be shown and use it to determine when the user is finished and the `openModalType` should change to `done`.
   */
  totalQuestions?: number;
  /**
   * Query arguments required to determine the dealership and the question types initialized for the session.
   * OrganizationId: The current organization's ID that the user is answering questions for.
   * type: QuestionType - The type of questions initialized (onboarding/standard)
   */
  organizationQueryArguments?: CheckRiskRegistryAnswerInput;
  /**
   * An array of all the remaining organizations and their respective questions that are still needing to be answered after the current organization's questions are answered.
   */
  remainingOrganizationQuestions?: CheckRiskRegistryResponse[];
  /**
   * When true, this means that the dealer has no questions that need to be answered.
   */
  hasNoAnswers: boolean;
  /**
   * When true, the question modals should not be shown for that particular organization
   */
  isSilenced: boolean;
  /**
   * When true, this means the state is resetting to initialState
   * and 'openModalType' and 'closedOrgsWithQuestions' values are being cleared from session storage.
   */
  isResetting: boolean;
  /**
   * An array of all the organizations that have had their answers closed. This is to prevent the user from re-pulling the answers for organizations they have already closed or
   * skipped questions while answering after a refresh. Since the refresh rebuilds the context, these IDs are stored into the session storage so that a refresh doesn't force the
   * user to start all the way over.
   */
  closedOrgsWithQuestions?: Organization['id'][];
  /**
   * The current organization's ID that the user is answering questions for.
   */
  organizationId?: string;
  /**
   * Whether the context is being built from a different place than at the top of the context heirarchy prior to a user logging in. This is needed since we do a lot of specific logic
   * for the question modals surrounding the log-in that we don't want when we pop them up for the remediation on the Risk Registry screen.
   */
  isNotFromLogIn?: boolean;

  // #region Access Management
  /**
   * This answer is in the `Access Management` group and `Primary Identity Management` subgroup.
   */
  [QuestionShorthand.PrimaryIdentityManagementPrimarySolution]?: AnswerResponse<string>;
  /**
   * This answer is in the `Access Management` group and `Unused User Accounts` subgroup.
   */
  [QuestionShorthand.UnusedUserAccountsReviewedPastYear]?: AnswerResponse<string>;
  /**
   * This answer is in the `Access Management` group and `Unused User Accounts` subgroup.
   */
  [QuestionShorthand.UnusedUserAccountsReviewedDate]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Access Management` group and `Cloud Identity Protection` subgroup.
   */
  [QuestionShorthand.CloudBasedIdentityProtectionPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Access Management` group and `Cloud Identity Protection` subgroup.
   */
  [QuestionShorthand.CloudBasedIdentityProtectionProvider]?: AnswerResponse<string>;
  /**
   * This answer is in the `Access Management` group and `MFA For Cloud & Sensitive Info` subgroup.
   */
  [QuestionShorthand.MfaForCloudReviewedPastYear]?: AnswerResponse<string>;
  /**
   * This answer is in the `Access Management` group and `MFA For Cloud & Sensitive Info` subgroup.
   */
  [QuestionShorthand.MfaForCloudReviewedDate]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Access Management` group and `Password Management` subgroup.
   */
  [QuestionShorthand.PasswordManagementManagementTool]?: AnswerResponse<string>;
  /**
   * This answer is in the `Access Management` group and `Password Management` subgroup.
   */
  [QuestionShorthand.PasswordManagementEnforced]?: AnswerResponse<string>;
  /**
   * This answer is in the `Access Management` group and `Password Management` subgroup.
   */
  [QuestionShorthand.PasswordManagementRequirements]?: AnswerResponse<Record<string, boolean>>;
  // #endregion

  // #region Business and Compliance
  /**
   * This answer is in the `Business and Compliance` group and `Prior Security Breach` subgroup.
   */
  [QuestionShorthand.PriorSecurityBreachKnownBreach]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Prior Security Breach` subgroup.
   */
  [QuestionShorthand.PriorSecurityBreachDateOfBreach]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Business and Compliance` group and `Infrastructure Projects` subgroup.
   */
  [QuestionShorthand.InfrastructureProjectsPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Infrastructure Projects` subgroup.
   */
  [QuestionShorthand.InfrastructureProjectsDateOfOccurrence]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Business and Compliance` group and `Qualified Individual` subgroup.
   */
  [QuestionShorthand.QualifiedIndividualAppointed]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Security Personnel` subgroup.
   */
  [QuestionShorthand.SecurityPersonnelPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Security Personnel` subgroup.
   */
  [QuestionShorthand.SecurityPersonnelType]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Written ISP` subgroup.
   */
  [QuestionShorthand.WrittenIspReviewedPastYear]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Written ISP` subgroup.
   */
  [QuestionShorthand.WrittenIspReviewedDate]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Business and Compliance` group and `Incident Response Plan` subgroup.
   */
  [QuestionShorthand.IncidentResponsePlanReviewedPastYear]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Incident Response Plan` subgroup.
   */
  [QuestionShorthand.IncidentResponsePlanReviewedDate]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Business and Compliance` group and `Technology Security Policies` subgroup.
   */
  [QuestionShorthand.TechnologySecurityPoliciesPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Technology Security Policies` subgroup.
   */
  [QuestionShorthand.TechnologySecurityPoliciesReviewed]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Technology Security Policies` subgroup.
   */
  [QuestionShorthand.TechnologySecurityPoliciesReviewedDate]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Business and Compliance` group and `User Security Policies` subgroup.
   */
  [QuestionShorthand.UserSecurityPoliciesPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `User Security Policies` subgroup.
   */
  [QuestionShorthand.UserSecurityPoliciesReviewed]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `User Security Policies` subgroup.
   */
  [QuestionShorthand.UserSecurityPoliciesReviewedDate]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Business and Compliance` group and `Vendor Risk` subgroup.
   */
  [QuestionShorthand.VendorRiskReviewedPastYear]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Vendor Risk` subgroup.
   */
  [QuestionShorthand.VendorRiskReviewedDate]?: AnswerResponse<Date>;
  /**
   * This answer is in the `Business and Compliance` group and `Data Storage` subgroup.
   */
  [QuestionShorthand.DataStorageStorageLocation]?: AnswerResponse<Record<string, boolean>>;
  /**
   * This answer is in the `Business and Compliance` group and `Critical Data` subgroup.
   */
  [QuestionShorthand.CriticalDataStoredOutside]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Critical Data` subgroup.
   */
  [QuestionShorthand.CriticalDataStorageLocation]?: AnswerResponse<Record<string, boolean>>;
  /**
   * This answer is in the `Business and Compliance` group and `Critical Data` subgroup.
   */
  [QuestionShorthand.CriticalDataDataType]?: AnswerResponse<Record<string, boolean>>;
  /**
   * This answer is in the `Business and Compliance` group and `BYOD Practices` subgroup.
   */
  [QuestionShorthand.ByodPracticesAllowed]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `BYOD Practices` subgroup.
   */
  [QuestionShorthand.ByodPracticesCriticalInfo]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Local Admin Permissions` subgroup.
   */
  [QuestionShorthand.LocalAdminPermissionsGranted]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Local Admin Permissions` subgroup.
   */
  [QuestionShorthand.LocalAdminPermissionsMfaRequired]?: AnswerResponse<string>;
  /**
   * This answer is in the `Business and Compliance` group and `Critical Systems List` subgroup.
   */
  [QuestionShorthand.CriticalSystemListDocumented]?: AnswerResponse<string>;
  // #endregion

  // #region Network Management
  /**
   * This answer is in the `Network Management` group and `Remote Access` subgroup.
   */
  [QuestionShorthand.RemoteAccessAllow]?: AnswerResponse<string>;
  /**
   * This answer is in the `Network Management` group and `Remote Access` subgroup.
   */
  [QuestionShorthand.RemoteAccessMfa]?: AnswerResponse<string>;
  /**
   * This answer is in the `Network Management` group and `Remote Access` subgroup.
   */
  [QuestionShorthand.RemoteAccessCriticalDevices]?: AnswerResponse<string>;
  /**
   * This answer is in the `Network Management` group and `Pen. Testing and Vulnerability Scans` subgroup.
   */
  [QuestionShorthand.PenTestingAndVulnerabilityScansExternal]?: AnswerResponse<string>;
  /**
   * This answer is in the `Network Management` group and `Pen. Testing and Vulnerability Scans` subgroup.
   */
  [QuestionShorthand.PenTestingAndVulnerabilityScansCadence]?: AnswerResponse<string>;
  /**
   * This answer is in the `Network Management` group and `Vendor Endpoint Access` subgroup.
   */
  [QuestionShorthand.VendorEndpointAccessAllowed]?: AnswerResponse<string>;
  /**
   * This answer is in the `Network Management` group and `Vendor Endpoint Access` subgroup.
   */
  [QuestionShorthand.VendorEndpointAccessMfa]?: AnswerResponse<string>;
  // #endregion

  // #region Device Management
  /**
   * This answer is in the `Device Management` group and `Asset Inventory` subgroup
   */
  [QuestionShorthand.AssetInventoryReviewedPastYear]?: AnswerResponse<string>;
  /**
   * This answer is in the `Device Management` group and `Asset Inventory` subgroup
   */
  [QuestionShorthand.AssetInventoryReviewedDate]?: AnswerResponse<Date>;
  // #endregion

  // #region Endpoint Security
  /**
   * This answer is in the `Endpoint Security` group and `Patch Management` subgroup.
   */
  [QuestionShorthand.PatchManagementPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Endpoint Security` group and `System Patching` subgroup.
   */
  [QuestionShorthand.SystemPatchingPatchedRegularly]?: AnswerResponse<string>;
  /**
   * This answer is in the `Endpoint Security` group and `Vulnerability Tracking` subgroup.
   */
  [QuestionShorthand.VulnerabilityTrackingPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Endpoint Security` group and `Endpoint Protection` subgroup. This is an `Onboarding` question answer.
   */
  [QuestionShorthand.EndpointProtectionPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Endpoint Security` group and `Endpoint Protection` subgroup. This is an `Onboarding` question answer.
   */
  [QuestionShorthand.EndpointProtectionLogging]?: AnswerResponse<string>;
  /**
   * This answer is in the `Endpoint Security` group and `Endpoint Protection` subgroup. This is an `Onboarding` question answer.
   */
  [QuestionShorthand.EndpointProtectionWasAllDevices]?: AnswerResponse<string>;
  /**
   * This answer is in the `Endpoint Security` group and `Endpoint Protection` subgroup. This is a `Standard` question answer.
   */
  [QuestionShorthand.EndpointProtectionIsAllDevices]?: AnswerResponse<string>;
  // #endregion

  // #region Email Security
  /**
   * This answer is in the `Email Security` group and `Corporate Email Domain` subgroup.
   */
  [QuestionShorthand.CorporateEmailDomainPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `MFA For Email` subgroup.
   */
  [QuestionShorthand.MfaForEmailRequired]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Inbox Security` subgroup.
   */
  [QuestionShorthand.InboxSecurityInactiveMailboxRemoved]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Inbox Security` subgroup.
   */
  [QuestionShorthand.InboxSecuritySharedMailboxReviewed]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Email Filter` subgroup.
   */
  [QuestionShorthand.EmailFilterPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Email Filter` subgroup.
   */
  [QuestionShorthand.EmailFilterTool]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Email Configurations` subgroup.
   */
  [QuestionShorthand.EmailConfigurationsEnforced]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Email Configurations` subgroup.
   */
  [QuestionShorthand.EmailConfigurationsDkim]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Email Configurations` subgroup.
   */
  [QuestionShorthand.EmailConfigurationsSpf]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Email Configurations` subgroup.
   */
  [QuestionShorthand.EmailConfigurationsDmarc]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Security Awareness Training` subgroup.
   */
  [QuestionShorthand.SecurityAwarenessTrainingPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Security Awareness Training` subgroup.
   */
  [QuestionShorthand.SecurityAwarenessTrainingEnrollmentTimeline]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Security Awareness Training` subgroup.
   */
  [QuestionShorthand.SecurityAwarenessTrainingCadence]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Phishing Simulation` subgroup.
   */
  [QuestionShorthand.PhishingSimulationPresent]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Phishing Simulation` subgroup.
   */
  [QuestionShorthand.PhishingSimulationEnrollmentTimeline]?: AnswerResponse<string>;
  /**
   * This answer is in the `Email Security` group and `Phishing Simulation` subgroup.
   */
  [QuestionShorthand.PhishingSimulationCadence]?: AnswerResponse<string>;
  // #endregion
}

/**
 * The type for the dispatch provider as well as the dispatch function itself.
 */
export type RiskAssessmentQuestionsDispatchState = Dispatch<RiskAssessmentQuestionsActions>;

/**
 * The context type for the RiskAssessmentQuestions state context. It can be undefined so that
 * we can verify anything attempting to consume the context is a child of the context.
 */
export type RiskAssessmentQuestionsState = RiskAssessmentQuestionsContextState | undefined;

/**
 * The context type for the RiskAssessmentQuestions dispatch context. It can be undefined so that
 * we can verify anything attempting to use the dispatch is a child of the context.
 */
export type RiskAssessmentQuestionsDispatch = RiskAssessmentQuestionsDispatchState | undefined;

/**
 * The RiskAssessmentQuestions state context type that is returned after the creation of the state context.
 */
export type RiskAssessmentQuestionsStateContextType = Context<RiskAssessmentQuestionsState>;

/**
 * The RiskAssessmentQuestions dispatch context type that is returned after the creation of the dispatch context.
 */
export type RiskAssessmentQuestionsDispatchContextType = Context<RiskAssessmentQuestionsDispatch>;
// #endregion

// #region Reducer
/**
 * The RiskAssessmentQuestions dispatch types used by our {@link ActionMap} type to distinguish what payload the
 * dispatch action will require.
 */
export enum RiskAssessmentQuestionsDispatchTypes {
  /**
   * This dispatch is used to update any number of question answers in the context based on what is passed in
   * via the dispatch.
   */
  UpdateQuestionAnswers = 'Update Question Answers',
  /**
   * This dispatch is used to increment the context's active step, and change the `openModalType` to `done` if
   * there are no more steps to transition to.
   */
  IncrementActiveStep = 'Increment Active Step',
  /**
   * This dispatch is used to decrement the context's active step. If the user is on the `done` modal, we revert the
   * `openModalType` property in the context to the `question` state.
   */
  DecrementActiveStep = 'Decrement Active Step',
  /**
   * This dispatch is used to set the state and session's active step to a specific number. For instance,
   * if the user closes the modal, we set the state and session active step to the modal's active step before close.
   */
  SetActiveStep = 'Set Active Step',
  /**
   * This dispatch sets the `openModalType` to some other passed in value. This is used primarily to close and/or never
   * show the modals again for the session.
   */
  SetOpenQuestionModal = 'Set Open Question Modal',
  /**
   * This dispatch sets the `organizationQueryArguments` to the value determined during app initialization.
   * These arguments are used as OrganizationInput in the checkRiskRegistryAnswers query
   */
  SetOrgQueryArguments = 'Set Org Query Arguments',
  /**
   * This dispatch is used to set how many questions the user will need to answer. Since we don't know when the context is
   * created, this dispatch happens after we have calculated all the questions that the user needs to be answered and shown.
   */
  SetTotalQuestions = 'Set Total Questions',
  /**
   * This dispatch partially resets RiskAssessmentQuestionsContext to initialState
   * after clearing 'openModalType' and 'closedOrgsWithQuestions' values from session storage.
   */
  ResetSessionAndState = 'Reset Session And State',
  /**
   * This dispatch clears the isResetting value of RiskAssessmentQuestionsContext setting it to false.
   */
  ClearIsResetting = 'Clear Is Resetting',
  /**
   * This dispatch is used to initialize the context with the answers for questions for each organization that they need
   * to answer questions for. Once the data has been dispatched to the context, we flip the `isInitialized` flag so that
   * the question modals can be shown.
   */
  Initialize = 'Initialize',
  /**
   * This dispatch is used to update the question answer objects in the context for when an ID in the database has been created
   * or deleted so that we can correctly associate the context value with the new database entry or dis-associate it with the now
   * deleted entry.
   */
  UpdateQuestionAnswerIds = 'Update Question Answer IDs',
  /**
   * This dispatch is used to switch the user to a new dealer after they have finished answering questions for a different dealer.
   * This dispatch does effectively the same logic as the initialize dispatch when inserting all the answers into the context,
   * but without some of the overhead that is needed when we first initialize the context.
   */
  SwitchToNextDealer = 'Switch to Next Dealer',
  /**
   * This dispatch is used to partially reset RiskAssessmentQuestionsContext when the close button on the Done modal is clicked.
   * It resets activeStep and previousActiveStep to zero.
   * In addition, it resets organizationId and remainingOrganizationsQuestions to null IF all questions have been answered.
   * Ensures the status banner and question modals are not shown when there are no pending questions.
   */
  ResetQuestionState = 'Reset State Partially',
}

/**
 * An object mapping each dispatch type to the payload required for that dispatch action.
 */
export interface RiskAssessmentQuestionsPayload {
  [RiskAssessmentQuestionsDispatchTypes.UpdateQuestionAnswers]: Partial<{
    [Key in QuestionShorthand]?: NonNullable<RiskAssessmentQuestionsContextState[Key]> extends AnswerResponse<infer U>
      ? U
      : never;
  }>;
  [RiskAssessmentQuestionsDispatchTypes.IncrementActiveStep]: never;
  [RiskAssessmentQuestionsDispatchTypes.DecrementActiveStep]: never;
  [RiskAssessmentQuestionsDispatchTypes.SetActiveStep]: number;
  [RiskAssessmentQuestionsDispatchTypes.SetOrgQueryArguments]: CheckRiskRegistryAnswerInput;
  [RiskAssessmentQuestionsDispatchTypes.ResetQuestionState]: never;
  [RiskAssessmentQuestionsDispatchTypes.ResetSessionAndState]: never;
  [RiskAssessmentQuestionsDispatchTypes.ClearIsResetting]: never;
  [RiskAssessmentQuestionsDispatchTypes.SetOpenQuestionModal]: OpenQuestionModalType;
  [RiskAssessmentQuestionsDispatchTypes.SetTotalQuestions]: number;
  [RiskAssessmentQuestionsDispatchTypes.Initialize]: CheckRiskRegistryData[];
  [RiskAssessmentQuestionsDispatchTypes.UpdateQuestionAnswerIds]: Partial<{
    [Key in QuestionShorthand]?: NonNullable<RiskAssessmentQuestionsContextState[Key]> extends AnswerResponse<infer U>
      ? AnswerResponse<U>['answerId']
      : never;
  }>;
  [RiskAssessmentQuestionsDispatchTypes.SwitchToNextDealer]: never;
}

/**
 * The union type of dispatch actions that results from using {@link ActionMap}.
 */
export type RiskAssessmentQuestionsActions =
  ActionMap<RiskAssessmentQuestionsPayload>[keyof ActionMap<RiskAssessmentQuestionsPayload>];

/**
 * The RiskAssessmentQuestions reducer function type definition. Primarily used by any `useReducer`
 * that uses the function or the function itself.
 */
export type RiskAssessmentQuestionsReducerFunction = TypedFunction<
  [RiskAssessmentQuestionsContextState, RiskAssessmentQuestionsActions],
  RiskAssessmentQuestionsContextState
>;
// #endregion

// #region Provider
/**
 * The object types for the props of the RiskAssessmentQuestions provider component.
 */
export interface RiskAssessmentQuestionsProviderProps {
  /**
   * When this is true, this means that the context is not being created from log-in and should not do the initialization logic.
   */
  isNotFromLogin?: boolean;
  /**
   * Any element that will render as a child of the RiskAssessmentQuestions context.
   */
  children?: ReactNode;
}

/**
 * This type map is used to indicate to the `getSessionStorageValue` function what type we
 * expect it to return.
 * It also indicates to the function what type of type checking it will need to do so we
 * can verify that the values retrieved from the session storage are of the correct type.
 */
export interface RiskAssessmentPossibleSessionStorageTypes {
  number: number;
  never: 'never';
  stringArray: string[];
}
// #endregion

// #region Hooks
/**
 * The function type for the `useRiskAssessmentQuestionsState` hook. Takes no arguments and returns the current RiskAssessmentQuestions state.
 */
export type RiskAssessmentQuestionsStateHook = TypedFunction<[void], RiskAssessmentQuestionsContextState>;

/**
 * Note: I used an interface for a single returned function because it ensures the name of the function will be the same across each use of the hook when
 * destructured from the returned values of the hook.
 */
export interface RiskAssessmentQuestionsDispatchHookReturn {
  /**
   * The named function that is returned to make dispatch calls to the context.
   */
  riskAssessmentQuestionsDispatch: RiskAssessmentQuestionsDispatchState;
}

/**
 * The function type for the `useRiskAssessmentQuestionsDispatch` hook. Takes no arguments and returns a dispatch function for the RiskAssessmentQuestions context.
 */
export type RiskAssessmentQuestionsDispatchHook = TypedFunction<[void], RiskAssessmentQuestionsDispatchHookReturn>;

/**
 * The props for the returned `getModalActionButtons` function on the `useGetModalActionButtons` hook.
 */
export interface GetModalActionButtonsProps<Form extends object> {
  /**
   * The logic that should happen when the user clicks the `Next` button, prior to the form being submitted.
   */
  onNext?: VoidFunction;
  /**
   * The logic that should happen when the user clicks the `Previous` button, prior to the form being submitted.
   */
  onPrevious?: VoidFunction;
  /**
   * Whether the logic to decrement the context's active step when clicking the previous button should be suppressed. This is used by
   * the modals that were split into multiple parts without actually changing the step that the user was on while answering the questions.
   */
  suppressDecrementStep?: boolean;
  /**
   * The form object that should be used for when we attempt to submit the form with the `Next` button.
   */
  form: UseFormReturn<Form, any, Form>;
  /**
   * The `handleSubmit` function with all the form submission logic that will be used when the user clicks the `Next` button.
   */
  onSubmit: TypedFunction<[BaseSyntheticEvent?], Promise<void>>;
}

/**
 * Note: I used an interface for a single returned function because it ensures the name of the function will be the same across each use of the hook when
 * destructured from the returned values of the hook.
 */
export interface GetModalActionButtonsHookReturn<Form extends object> {
  /**
   * This function takes in an object of {@link GetModalActionButtonsProps} props and returns an array
   * of {@link ModalActionButton} elements that can be inserted directly into the modal body layout component.
   */
  getModalActionButtons: TypedFunction<GetModalActionButtonsProps<Form>, ModalActionButton[]>;
}

/**
 * The props object that is used by the function returned from the `useSendModalAnswers` hook.
 *
 * @extends AnswerResponse - {@link AnswerResponse} description.
 */
export interface SendModalAnswersProps extends Pick<AnswerResponse<string>, 'answerId' | 'answer'> {
  /**
   * The string value of any of the {@link QuestionShorthand} enum values so that we can reference the
   * context value using it when doing the Create/Update/Delete mutations.
   */
  questionShorthand: `${QuestionShorthand}`;
}

/**
 * The response returned by the `CreateRiskRegistryAnswer` mutation.
 */
export interface CreateRiskRegistryAnswerResponse {
  /**
   * The ID of the newly created answer record in the RiskRegistryAnswers table.
   */
  id: string;
  /**
   * An object from the RiskRegistryQuestions table that has been linked to through the answers on the query.
   */
  question: {
    /**
     * The question shorthand that we use to update the context's answer record with the database ID so that any
     * changes can reference the exact record for update or delete mutations.
     */
    questionShorthand: QuestionShorthand;
  };
}

/**
 * The response returned by the `RemoveRiskRegistryAnswer` mutation.
 */
export interface RemoveRiskRegistryAnswerResponse {
  /**
   * An object from the RiskRegistryQuestions table that has been linked to through the answers on the query.
   */
  question: {
    /**
     * The question shorthand that we use to remove the answer and database related values of the now deleted
     * answer from the context.
     */
    questionShorthand: QuestionShorthand;
  };
}

/**
 * Note: I used an interface for a single returned function because it ensures the name of the function will be the same across each use of the hook when
 * destructured from the returned values of the hook.
 */
export interface SendModalAnswersHookReturn {
  sendModalAnswers: TypedFunction<[SendModalAnswersProps[]], Promise<void>>;
}

/**
 * The function type definition for the `useSendModalAnswers` hook.
 */
export type SendModalAnswersHook = TypedFunction<void, SendModalAnswersHookReturn>;

/**
 * The object for the data returned from the RiskRegistryAnswers table.
 */
export interface CheckRiskRegistryResponseAnswers {
  /**
   * The answer to a question as either a string, date as a string, or a JSON blob that has been stringified.
   */
  answer?: string | null;
  /**
   * The date as a string of when the answer will expire.
   */
  expiresAt?: string | null;
  /**
   * An object from the RiskRegistryQuestions table that has been linked to through the answers on the query.
   */
  question: {
    /**
     * The question shorthand for the question that is related to the specific answer. This is how we know
     * what modal question to put the answer into.
     */
    questionShorthand: QuestionShorthand;
  };
  /**
   * An object from the Organizations table that has been linked to through the answers on the query.
   */
  organization: {
    /**
     * The date as a string for when the dealership will finish the onboarding phase and only see questions/answers
     * for the standard flow going forward.
     */
    onboardingUntil?: string | null;
  };
  /**
   * The id in the RiskRegistryAnswers table so that we can directly update an answer entry when applicable.
   */
  id: string;
}

/**
 * The returned object from the CheckRiskRegistryAnswers Query.
 */
export interface CheckRiskRegistryResponse {
  /**
   * The dealership's ID.
   */
  organizationId: string;
  hasNoAnswers: boolean;
  isSilenced: boolean;
  /**
   * An array of all the answer objects returned for a specific organization.
   */
  answers?: Array<CheckRiskRegistryResponseAnswers | null> | null;
}

/**
 * The final object format after we have filtered, mapped, and sorted the data returned by the CheckRiskRegistryAnswers query that includes
 * the date as a string of when the dealer will finish their onboarding process.
 */
export type CheckRiskRegistryData = CheckRiskRegistryResponse & Pick<Organization, 'onboardingUntil'>;

/**
 * The exact return type of the CheckRiskRegistryAnswers query. This type is explicitly used to simplify the complicated type
 * into a single named type that can be re-used in the context as needed.
 */
export type CheckRiskRegistryExactResponse = QueryResult<
  CheckRiskRegistryAnswersQuery,
  Exact<{
    organizationInputs: Array<InputMaybe<CheckRiskRegistryAnswerInput>> | InputMaybe<CheckRiskRegistryAnswerInput>;
  }>
>;

/**
 * Note: I used an interface for a single returned function because it ensures the name of the function will be the same across each use of the hook when
 * destructured from the returned values of the hook.
 */
export interface InitializeRiskRegistryQuestionsReturn {
  /**
   * A function that initializes the context by taking in some number of objects containing the `organizationInputs` objects that have data pertaining
   * to organization IDs and what type of questions we expect back. We then filter out the organizations that aren't relevant and sort them. Some filtering
   * is omitted if `isNotFromLogin` is true, but by default the prop is false.
   *
   * @param answerInput - An object or an array of objects that contains the organization's ID and question type to return which will determine what information is pulled.
   * @param isNotFromLogin - When true, we skip filtering out organizations and use all data that was returned by the API. By default this is off
   * since we want to filter out certain returned data when we initialize the context on log-in. This is primarily used for remediation on the
   * Risk Registry screen.
   */
  initializeRiskRegistryQuestions: TypedFunction<
    [answerInput: CheckRiskRegistryAnswerInput | CheckRiskRegistryAnswerInput[], isNotFromLogin?: boolean],
    void
  >;
}

/**
 * Function type definition for the `useInitializeRiskRegistryQuestions` hook.
 */
export type InitializeRiskRegistryQuestionsHook = TypedFunction<void, InitializeRiskRegistryQuestionsReturn>;

/**
 * Return type definition for the `useHasPendingQuestions` hook.
 */
export type HasPendingQuestionsReturn = {
  /**
   * Checks if there are pending or unanswered questions.
   *
   * @returns A promise that resolves to `true` if pending questions exist.
   * If an error occurs, the promise resolves to the last known state (`hasPendingQuestions`) as a fallback value.
   */
  checkPendingQuestions: () => Promise<boolean>;
};

// #endregion
